import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, CheckCircle2, Loader2, RefreshCw, CreditCard,
} from 'lucide-react';

const API = '/api';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/**
 * Read-only payment health card for the admin Control Center.
 *
 * Calls GET /api/admin/control-center/payment-diagnostic which itself
 * never touches Stripe (no Dashboard API, no price mutation, no
 * checkout creation). Pure aggregate over funnel_events +
 * stripe_webhook_log buffer.
 */
export default function PaymentDiagnosticCard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/admin/control-center/payment-diagnostic`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  if (loading && !data) {
    return (
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center gap-2 text-sm text-white/65"
        data-testid="payment-diagnostic-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Payment health laden&hellip;
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-500/10 border border-rose-400/30 rounded-2xl p-4 text-sm text-rose-200"
        data-testid="payment-diagnostic-error">
        Payment diagnostic kon niet laden: {err}
      </div>
    );
  }
  if (!data) return null;

  const c = data.counts_7d || {};
  const hasZeroPayments = (c.payment_completed || 0) === 0;
  const tone = hasZeroPayments
    ? 'border-amber-400/30 bg-amber-500/5'
    : 'border-emerald-400/30 bg-emerald-500/5';

  return (
    <section className={`rounded-2xl border ${tone} p-4 sm:p-5 shadow-2xl text-white`}
      data-testid="payment-diagnostic-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-sm font-semibold tracking-tight flex items-center gap-2 text-white" style={{ fontFamily: 'Outfit' }}>
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <CreditCard className="w-4 h-4" />
          </span>
          Payment health <span className="text-[10px] uppercase tracking-wider text-white/45 ml-1">(read-only)</span>
        </h3>
        <button onClick={load}
          data-testid="payment-diagnostic-refresh"
          className="text-[11px] text-white/65 hover:text-emerald-300 inline-flex items-center gap-1 transition-colors">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Vernieuw
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid="payment-diagnostic-payment-count">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Payments 7d</div>
          <div className="text-xl font-semibold mt-0.5 text-white">{c.payment_completed ?? 0}</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid="payment-diagnostic-checkout-count">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Checkouts 7d</div>
          <div className="text-xl font-semibold mt-0.5 text-white">{c.checkout_started ?? 0}</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid="payment-diagnostic-click-count">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Pay-clicks 7d</div>
          <div className="text-xl font-semibold mt-0.5 text-white">{c.payment_clicked ?? 0}</div>
        </div>
        <div className={`rounded-lg p-2 border ${((c.payment_failed||0)>0) ? 'bg-rose-500/10 border-rose-400/30 text-rose-200' : 'bg-white/5 border-white/10 text-white'}`}
          data-testid="payment-diagnostic-failed-count">
          <div className="text-[10px] uppercase tracking-wider opacity-70">Failed 7d</div>
          <div className="text-xl font-semibold mt-0.5">{c.payment_failed ?? 0}</div>
        </div>
      </div>

      <div className="text-[11px] space-y-1 text-white/75">
        <div data-testid="payment-diagnostic-last-payment">
          <strong className="text-white">Laatste payment_completed:</strong> {fmtDate(data.last_events?.payment_completed?.tracked_at)}
        </div>
        <div data-testid="payment-diagnostic-last-checkout">
          <strong className="text-white">Laatste checkout_started:</strong> {fmtDate(data.last_events?.checkout_started?.tracked_at)}
        </div>
        <div data-testid="payment-diagnostic-last-click">
          <strong className="text-white">Laatste payment_clicked:</strong> {fmtDate(data.last_events?.payment_clicked?.tracked_at)}
        </div>
        <div data-testid="payment-diagnostic-last-failed">
          <strong className="text-white">Laatste payment_failed:</strong> {fmtDate(data.last_events?.payment_failed?.tracked_at)}
        </div>
        <div data-testid="payment-diagnostic-webhook-log">
          <strong className="text-white">stripe_webhook_log:</strong> {data.stripe_webhook_log?.count ?? 0} entries
          {data.stripe_webhook_log?.last && (
            <> &middot; laatste: {data.stripe_webhook_log.last.event_type} ({fmtDate(data.stripe_webhook_log.last.received_at)})</>
          )}
        </div>
        {data.price_config && (
          <div data-testid="payment-diagnostic-price-config">
            <strong className="text-white">Prijs-config:</strong>{' '}
            intro €9,95 {data.price_config.intro_price_resolvable === false
              ? <span className="text-rose-300">✗ niet vindbaar</span>
              : <span className="text-emerald-300">✓ vindbaar</span>}
            {' · '}
            regular €14,95 {data.price_config.regular_price_resolvable === false
              ? <span className="text-rose-300">✗ niet vindbaar</span>
              : <span className="text-emerald-300">✓ vindbaar</span>}
          </div>
        )}
      </div>

      {Array.isArray(data.warnings) && data.warnings.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="payment-diagnostic-warnings">
          {data.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-[11px] bg-amber-500/10 border border-amber-400/30 rounded-md p-2 text-amber-200">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {(!data.warnings || data.warnings.length === 0) && (
        <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-emerald-300"
          data-testid="payment-diagnostic-ok">
          <CheckCircle2 className="w-3.5 h-3.5" /> Geen waarschuwingen
        </div>
      )}

      <p className="text-[10px] text-white/45 mt-3">
        Bron: funnel_events + stripe_webhook_log buffer. Géén Stripe Dashboard API,
        géén prijswijziging, géén mutatie.
      </p>
    </section>
  );
}
